import { useState, useEffect } from "react";
import api from "../../services/api";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const REMINDER_TYPES = [
  {
    key: "j_minus_30",
    label: "J-30",
    title: "Renouvellement prochain",
    desc: "Information envoyée 30 jours avant l'échéance",
    color: "#185fa5",
    bg: "#185fa522",
    defaultTemplate: "Bonjour {{client}},\n\nVotre abonnement \"{{offre}}\" arrive à échéance le {{date}}.\n\nMontant : {{montant}} € TTC\n\nCordialement,\nL'équipe FinanceApp",
  },
  {
    key: "j_minus_15",
    label: "J-15",
    title: "Rappel renouvellement",
    desc: "Rappel avec lien de paiement 15 jours avant",
    color: "#1a9e72",
    bg: "#1a9e7222",
    defaultTemplate: "Bonjour {{client}},\n\nVotre abonnement \"{{offre}}\" est à renouveler dans 15 jours (le {{date}}).\n\nMontant : {{montant}} € TTC\n\nPour régler : {{lien_paiement}}\n\nCordialement",
  },
  {
    key: "j_minus_7",
    label: "J-7",
    title: "Rappel urgent",
    desc: "Rappel urgent 7 jours avant l'échéance",
    color: "#ba7517",
    bg: "#ba751722",
    defaultTemplate: "Bonjour {{client}},\n\nURGENT : Votre abonnement \"{{offre}}\" expire dans 7 jours (le {{date}}).\n\nMontant : {{montant}} € TTC\n\nAction requise : {{lien_paiement}}\n\nCordialement",
  },
  {
    key: "j_minus_3",
    label: "J-3",
    title: "Dernier rappel",
    desc: "Dernier rappel 3 jours avant expiration",
    color: "#e24b4a",
    bg: "#e24b4a22",
    defaultTemplate: "Bonjour {{client}},\n\nDERNIER RAPPEL : Votre abonnement \"{{offre}}\" expire dans 3 jours.\n\nSans action de votre part, il sera automatiquement suspendu.\n\nRéglez maintenant : {{lien_paiement}}\n\nCordialement",
  },
  {
    key: "j_plus_1",
    label: "J+1",
    title: "Abonnement expiré",
    desc: "Notification d'expiration + proposition de renouvellement",
    color: "#8b5cf6",
    bg: "#8b5cf622",
    defaultTemplate: "Bonjour {{client}},\n\nVotre abonnement \"{{offre}}\" a expiré hier.\n\nPour le renouveler et continuer à bénéficier de nos services :\n{{lien_renouvellement}}\n\nCordialement",
  },
];

export default function RemindersTab({ currentCompanyId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(
    Object.fromEntries(REMINDER_TYPES.map(r => [r.key, true]))
  );
  const [templates, setTemplates] = useState(
    Object.fromEntries(REMINDER_TYPES.map(r => [r.key, r.defaultTemplate]))
  );
  const [expandedKey, setExpandedKey] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.subscriptions.list({ company_id: currentCompanyId })
      .then(() => {})
      .catch(console.error)
      .finally(() => setLoading(false));

    // Load reminder history
    const savedEnabled = localStorage.getItem(`sub_reminders_enabled_${currentCompanyId}`);
    const savedTemplates = localStorage.getItem(`sub_reminders_templates_${currentCompanyId}`);
    if (savedEnabled)   setEnabled(JSON.parse(savedEnabled));
    if (savedTemplates) setTemplates(JSON.parse(savedTemplates));
  }, [currentCompanyId]);

  function handleSave() {
    localStorage.setItem(`sub_reminders_enabled_${currentCompanyId}`, JSON.stringify(enabled));
    localStorage.setItem(`sub_reminders_templates_${currentCompanyId}`, JSON.stringify(templates));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const VARS = ["{{client}}", "{{offre}}", "{{date}}", "{{montant}}", "{{lien_paiement}}", "{{lien_renouvellement}}"];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Configuration des rappels automatiques</h3>
        <div style={{ flex: 1 }} />
        {saved && <span style={{ fontSize: 12, color: "var(--g)", fontWeight: 600 }}>✅ Sauvegardé</span>}
        <button className="btn primary" onClick={handleSave}>Sauvegarder la configuration</button>
      </div>

      <div style={{ marginBottom: 16, padding: "10px 14px", background: "#185fa511", border: "1px solid #185fa533", borderRadius: 8, fontSize: 12, color: "#185fa5" }}>
        <strong>Variables disponibles :</strong> {VARS.join(" · ")}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {REMINDER_TYPES.map(rt => (
          <div key={rt.key} className="card" style={{ border: `1px solid ${enabled[rt.key] ? rt.color + "44" : "transparent"}`, transition: "border 0.15s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: rt.color, background: rt.bg, padding: "4px 10px", borderRadius: 8, minWidth: 48, textAlign: "center" }}>{rt.label}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{rt.title}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{rt.desc}</div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <div style={{ position: "relative", width: 40, height: 22 }}>
                  <input type="checkbox" checked={enabled[rt.key]} onChange={e => setEnabled(p => ({ ...p, [rt.key]: e.target.checked }))} style={{ opacity: 0, width: 0, height: 0, position: "absolute" }} />
                  <div onClick={() => setEnabled(p => ({ ...p, [rt.key]: !p[rt.key] }))} style={{
                    position: "absolute", inset: 0, borderRadius: 11,
                    background: enabled[rt.key] ? rt.color : "#94a3b8", cursor: "pointer", transition: "background 0.2s"
                  }}>
                    <div style={{
                      position: "absolute", top: 3, left: enabled[rt.key] ? 21 : 3,
                      width: 16, height: 16, borderRadius: "50%", background: "#fff",
                      transition: "left 0.2s", boxShadow: "0 1px 3px #0003"
                    }} />
                  </div>
                </div>
                <span style={{ fontSize: 12, color: enabled[rt.key] ? rt.color : "var(--text-muted)", fontWeight: 600 }}>
                  {enabled[rt.key] ? "Activé" : "Désactivé"}
                </span>
              </label>
              <button className="btn" style={{ fontSize: 12 }} onClick={() => setExpandedKey(expandedKey === rt.key ? null : rt.key)}>
                {expandedKey === rt.key ? "▲ Fermer" : "✏️ Template"}
              </button>
            </div>

            {expandedKey === rt.key && (
              <div style={{ marginTop: 16, borderTop: "var(--border)", paddingTop: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Template email — {rt.title}
                </label>
                <textarea
                  className="input"
                  rows={8}
                  value={templates[rt.key]}
                  onChange={e => setTemplates(p => ({ ...p, [rt.key]: e.target.value }))}
                  style={{ resize: "vertical", fontFamily: "monospace", fontSize: 12, lineHeight: 1.6 }}
                />
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                  Utilisez les variables ci-dessus pour personnaliser le message automatiquement.
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  {VARS.map(v => (
                    <button key={v} className="btn" style={{ fontSize: 10, padding: "3px 8px", color: rt.color }}
                      onClick={() => setTemplates(p => ({ ...p, [rt.key]: p[rt.key] + v }))}>
                      {v}
                    </button>
                  ))}
                  <button className="btn" style={{ fontSize: 10, padding: "3px 8px", marginLeft: "auto", color: "#e24b4a" }}
                    onClick={() => setTemplates(p => ({ ...p, [rt.key]: rt.defaultTemplate }))}>
                    Réinitialiser
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Historique des rappels */}
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <span>📬</span> Historique des rappels envoyés
        </div>
        {loading ? (
          <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Chargement…</div>
        ) : (
          <div style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center", padding: 24 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
            Aucun rappel envoyé pour le moment.<br />
            Les rappels automatiques apparaîtront ici une fois configurés et traités.
          </div>
        )}
      </div>
    </div>
  );
}
